import React, { ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { Button, ButtonText, FormError, H3, Icon, Input, Message, Modal, ModalHeader } from 'components';
import { lineHeight, theme, toRem } from 'design';

// For Stacking Elements in a header
interface HeaderContentProps {
  gridGap?: number;
}

export const HeaderContent = styled.div<HeaderContentProps>`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(theme.space.md)};
  justify-items: center;
  width: 100%;

  ${({ gridGap }) =>
    gridGap &&
    css`
      grid-gap: ${toRem(gridGap)};
    `}

  > * {
    margin-bottom: 0;
    word-break: break-word;
  }
`;

export const StyledFormError = styled(FormError)`
  margin-top: ${toRem(theme.space.lg)};
  margin-bottom: 0;
  align-self: center;
`;

export const StyledErrorSection = styled.section`
  margin-top: ${toRem(theme.space.lg)};
`;

export const Subtitle = styled(H3)`
  font-weight: ${theme.font.weight.regular};
`;

export const InvitationNotice = styled(Message)`
  margin-bottom: ${toRem(theme.space['2xl'])};
  word-break: break-word;
`;

// Buttons
export const ReviewNowButton = styled(ButtonText)`
  margin-left: ${toRem(theme.space.xs)};
  color: ${theme.color.primary[500]};
`;

export const RemoveButton = styled(ButtonText)`
  color: ${theme.color.danger[50]};
`;

export const UnlinkButton = styled(ButtonText)`
  color: ${theme.color.primary[500]};
  text-decoration: none;
`;

// SelectUser
export const SelectUserModal = styled(Modal)`
  padding-bottom: 0;
`;

export const SelectUserModalHeader = styled(ModalHeader)`
  padding-bottom: ${toRem(theme.space.xl)};
`;

export const SearchInput = styled(Input)`
  align-items: center;
  text-align: center;
`;

export const LoadButton = styled(Button)`
  margin-top: ${toRem(theme.space.xl)};
`;

// Transaction Details
export const PayoutCardRow = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(theme.space.md)};
  justify-content: center;
  width: 100%;
  height: auto;
  margin-top: ${toRem(theme.space['2xl'])};

  @media (min-width: ${theme.breakpoint.xsmall}px) {
    grid-template-columns: repeat(auto-fit, minmax(0, ${toRem(185)}));
  }
`;

const PayoutCardIcon = styled(Icon).attrs({ icon: 'Tooltip' })`
  position: absolute;
  right: ${toRem(12.5)};
  bottom: ${toRem(12.5)};
  display: flex;
  align-self: center;
  width: ${toRem(16.5)};
  height: ${toRem(16.5)};
  margin-left: ${toRem(theme.space.xs)};
  fill: ${theme.color.neutral[200]};
  cursor: pointer;
  transition: fill ${theme.transition.icon};
`;

const PayoutCardLayout = styled.div`
  position: relative;
  display: flex;
  flex: 0 0 ${toRem(185)};
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: ${toRem(66)};
  border-radius: ${toRem(5.5)};
  background-color: ${theme.color.neutral[200]};
  color: ${theme.color.neutral[900]};

  label {
    margin-bottom: ${toRem(theme.space.sm)};
  }

  &:hover {
    ${PayoutCardIcon} {
      fill: ${theme.color.neutral[400]};
    }
  }
`;

const PayoutCardLoader = styled(Skeleton)`
  && {
    position: relative;
    display: flex;
    flex: 0 0 ${toRem(185)};
    flex-flow: column nowrap;
    justify-content: center;
    align-items: center;
    height: ${toRem(66)};
    border-radius: ${toRem(5.5)};
  }
`;

interface PayoutCardProps {
  children?: ReactNode;
  className?: string;
  isLoading?: boolean;
}

export const PayoutCard = ({ children, className, isLoading, ...rest }: PayoutCardProps) =>
  isLoading ? (
    <PayoutCardLoader />
  ) : (
    <PayoutCardLayout className={className} {...rest}>
      {children}
    </PayoutCardLayout>
  );

interface TimestampProps {
  date: string;
  time?: string;
}

const TimestampLayout = styled.div`
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-end;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  color: ${theme.color.neutral[900]};
`;

const Date = styled.span`
  position: relative;
  font-size: ${toRem(theme.font.size.sm)};
  line-height: ${lineHeight(theme.font.size.sm)};
  font-weight: ${theme.font.weight.medium};

  @media (min-width: ${theme.breakpoint.med}px) {
    font-size: ${toRem(theme.font.size.lg)};
    line-height: ${lineHeight(theme.font.size.lg)};
  }
`;

const Time = styled.span`
  position: relative;
  margin-top: ${toRem(theme.space.xs)};
  font-size: ${toRem(theme.font.size.sm)};
  line-height: ${lineHeight(theme.font.size.sm)};
  color: ${theme.color.neutral[700]};

  @media (min-width: ${theme.breakpoint.med}px) {
    margin-top: ${toRem(theme.space.sm)};
  }
`;

export const PayoutTimestamp = ({ date, time }: TimestampProps) => (
  <TimestampLayout>
    <Date>{date}</Date>
    {time && <Time>{time}</Time>}
  </TimestampLayout>
);
